import { useCallback, useEffect, useRef, useState } from 'react';
import { Crop, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ImageStoryBoundingBoxEditor } from '@/components/ImageStoryBoundingBoxEditor';
import {
  FULL_FRAME_CROP_BOX,
  resolveImageSrcForCanvasCrop,
  sceneImageUrlWithCropRevision,
} from '@/lib/imageStoryCrop';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';

/**
 * Review Storybuilder PDF pages: show extracted image + text; crop becomes the scene image.
 */
export function StorybuilderPageReviewPanel({
  projectId,
  onContinue,
  continuing = false,
}) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [draftText, setDraftText] = useState({});
  const [sheetSrcByPage, setSheetSrcByPage] = useState({});
  const cropRefs = useRef({});

  const loadPages = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/storybuilder/projects/${projectId}/pages`);
      const list = data?.pages || [];
      setPages(list);
      setDraftText(
        Object.fromEntries(list.map((p) => [p.id, p.text || ''])),
      );
    } catch (err) {
      toast.error(formatApiError(err, 'Could not load extracted pages'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  useEffect(() => {
    let cancelled = false;
    const urlsToRevoke = [];
    (async () => {
      const next = {};
      for (const page of pages) {
        const src = page.source_image_url || page.image_url;
        if (!src) continue;
        try {
          // eslint-disable-next-line no-await-in-loop
          const resolved = await resolveImageSrcForCanvasCrop(src);
          if (resolved && resolved !== src && resolved.startsWith('blob:')) {
            urlsToRevoke.push(resolved);
          }
          next[page.id] = resolved || mediaUrlForPlayback(src);
        } catch {
          next[page.id] = mediaUrlForPlayback(src);
        }
        if (cancelled) break;
      }
      if (!cancelled) setSheetSrcByPage(next);
    })();
    return () => {
      cancelled = true;
      urlsToRevoke.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [pages]);

  const savePage = async (page) => {
    const cropRef = cropRefs.current[page.id];
    let cropBox = page.crop_box || FULL_FRAME_CROP_BOX;
    if (cropRef?.getSceneCropUpdates) {
      const updates = cropRef.getSceneCropUpdates() || [];
      if (updates[0]?.cropNorm) cropBox = updates[0].cropNorm;
    }
    const text = draftText[page.id] ?? page.text ?? '';
    setSavingId(page.id);
    try {
      const { data } = await api.patch(
        `/storybuilder/projects/${projectId}/pages/${page.id}`,
        { text, crop_box: cropBox },
      );
      setPages((prev) => prev.map((p) => (p.id === page.id ? { ...p, ...data } : p)));
      cropRef?.clearPendingDrafts?.();
      toast.success(`Page ${page.segment_number} saved`);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save page'));
    } finally {
      setSavingId(null);
    }
  };

  const saveAllAndContinue = async () => {
    for (const page of pages) {
      // eslint-disable-next-line no-await-in-loop
      await savePage(page);
    }
    onContinue?.();
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-[#1f2635] bg-[#12151d] p-8 flex items-center justify-center gap-2 text-sm text-[#9CA3AF]">
        <Loader2 className="w-4 h-4 animate-spin text-[#62FAE3]" />
        Loading extracted pages…
      </div>
    );
  }

  if (!pages.length) {
    return (
      <div className="rounded-2xl border border-[#1f2635] bg-[#12151d] p-6 text-sm text-[#9CA3AF]">
        No pages extracted yet.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="storybuilder-page-review">
      <div className="rounded-2xl border border-[#1f2635] bg-[#12151d] p-5 space-y-2">
        <div className="flex items-center gap-2 text-[#62FAE3] text-xs font-mono uppercase tracking-widest">
          <Crop className="w-3.5 h-3.5" />
          Page review
        </div>
        <h2 className="text-lg font-semibold">Crop images &amp; edit text</h2>
        <p className="text-sm text-[#9CA3AF] leading-relaxed">
          Drag the box on each page image — the cropped region becomes the scene.
          Edit the extracted narration text, then continue to audio and video.
        </p>
        <div className="pt-2">
          <Button
            type="button"
            className="bg-[#62FAE3] text-black hover:bg-[#4de1c1]"
            disabled={continuing || Boolean(savingId)}
            onClick={saveAllAndContinue}
            data-testid="storybuilder-continue-btn"
          >
            {continuing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Continuing…
              </>
            ) : (
              <>Save crops &amp; continue</>
            )}
          </Button>
        </div>
      </div>

      {pages.map((page) => {
        const sheetSrc = sheetSrcByPage[page.id];
        const previewSrc = sceneImageUrlWithCropRevision(
          page.image_url || page.source_image_url,
          page,
        );
        const scenes = [
          {
            segment_number: page.segment_number,
            client_id: `page-${page.id}`,
            crop_box: page.crop_box || FULL_FRAME_CROP_BOX,
            image_url: page.image_url,
          },
        ];
        return (
          <div
            key={page.id}
            className="rounded-2xl border border-[#1f2635] bg-[#12151d]/90 p-4 sm:p-5 space-y-4"
            data-testid={`storybuilder-page-card-${page.segment_number}`}
          >
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold">Page {page.segment_number}</h3>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-[#1f2635] h-8"
                disabled={savingId === page.id || continuing}
                onClick={() => savePage(page)}
                data-testid={`storybuilder-save-page-${page.segment_number}`}
              >
                {savingId === page.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5 mr-1.5" /> Apply crop
                  </>
                )}
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_140px] gap-4">
              <div className="rounded-xl border border-[#1f2635] bg-[#0b0e13] overflow-hidden min-h-[200px]">
                {sheetSrc ? (
                  <ImageStoryBoundingBoxEditor
                    ref={(el) => {
                      cropRefs.current[page.id] = el;
                    }}
                    imageSrc={sheetSrc}
                    scenes={scenes}
                    className="p-2"
                  />
                ) : (
                  <div className="h-48 flex items-center justify-center text-xs text-[#6B7280]">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Preparing image…
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">Scene preview</p>
                {previewSrc ? (
                  <img
                    src={mediaUrlForPlayback(previewSrc)}
                    alt={`Page ${page.segment_number} scene`}
                    className="w-full rounded-lg border border-[#1f2635] object-contain max-h-40 bg-black"
                  />
                ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-[#9CA3AF]" htmlFor={`sb-text-${page.id}`}>
                Extracted text (narration)
              </label>
              <Textarea
                id={`sb-text-${page.id}`}
                value={draftText[page.id] ?? ''}
                onChange={(e) =>
                  setDraftText((prev) => ({ ...prev, [page.id]: e.target.value }))
                }
                rows={5}
                className="bg-[#0b0e13] border-[#1f2635] text-sm min-h-[100px]"
                data-testid={`storybuilder-page-text-${page.segment_number}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default StorybuilderPageReviewPanel;
